import { useState, useEffect } from "react";
import { Users, Zap, Clock, DollarSign, TrendingUp, CheckCircle2, LifeBuoy, Activity, Download } from "lucide-react";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import MetricCard from "@/components/MetricCard";
import StatusBadge from "@/components/StatusBadge";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const brl = (v) => `R$ ${Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`;
const RANGES = [7, 15, 30, 90];

const INTEGRATIONS = [
  { key: "ative", label: "Ative Apps" },
  { key: "lynx", label: "Lynx (PIX)" },
  { key: "mercadopago", label: "Mercado Pago" },
  { key: "telegram", label: "Telegram Bot" },
  { key: "deepseek", label: "Assistente IA" },
];

export default function AdminOverview() {
  const [days, setDays] = useState(30);
  const [a, setA] = useState(null);
  const [recent, setRecent] = useState([]);
  const [dl, setDl] = useState("");

  const download = async (kind) => {
    setDl(kind);
    try {
      const path = kind === "activations" ? "activations.csv" : "orders.csv";
      const { data } = await api.get(`/admin/export/${path}?days=${days}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = kind === "activations" ? `vulpix_ativacoes_${days}d.csv` : `vulpix_vendas_${days}d.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV gerado");
    } catch (e) { toast.error(apiError(e)); }
    finally { setDl(""); }
  };

  useEffect(() => { api.get(`/admin/analytics?days=${days}`).then(({ data }) => setA(data)).catch(() => {}); }, [days]);
  useEffect(() => { api.get("/admin/dashboard").then(({ data }) => setRecent(data.recent_activations || [])).catch(() => {}); }, []);

  if (!a) return <div className="text-muted-foreground">Carregando...</div>;
  const k = a.kpis;

  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-heading text-xl font-bold tracking-tight sm:text-2xl">Visão geral</h2>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => download("activations")} disabled={!!dl} data-testid="export-activations-btn"
            className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-sm transition-colors hover:bg-muted disabled:opacity-50">
            <Download className="h-4 w-4" /> {dl === "activations" ? "Gerando..." : "Exportar Ativações"}
          </button>
          <button onClick={() => download("orders")} disabled={!!dl} data-testid="export-orders-btn"
            className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-sm transition-colors hover:bg-muted disabled:opacity-50">
            <Download className="h-4 w-4" /> {dl === "orders" ? "Gerando..." : "Exportar Vendas"}
          </button>
          <div className="flex gap-1 rounded-lg border border-border p-1">
            {RANGES.map((r) => (
              <button key={r} onClick={() => setDays(r)} data-testid={`range-${r}`}
                className={`rounded-md px-3 py-1 text-sm transition-colors ${days === r ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>
                {r}d
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <MetricCard icon={DollarSign} label="Receita total" value={brl(k.revenue_total)} hint={`${brl(k.revenue_window)} nos ${a.days}d`} testid="kpi-revenue" />
        <MetricCard icon={Zap} label="Ativações" value={k.activations_total} hint={`${k.approved} aprovadas`} testid="kpi-activations" />
        <MetricCard icon={Activity} label="Taxa de sucesso" value={`${k.success_rate}%`} hint={`${k.failed} com erro`} testid="kpi-success" />
        <MetricCard icon={Clock} label="Pendentes" value={k.pending} testid="kpi-pending" />
        <MetricCard icon={Users} label="Novos usuários" value={k.new_users} hint={`nos ${a.days}d`} testid="kpi-newusers" />
        <MetricCard icon={LifeBuoy} label="Tickets abertos" value={k.tickets_open} testid="kpi-tickets" />
        <MetricCard icon={TrendingUp} label="Ativações aprovadas" value={k.approved} testid="kpi-approved" />
        <MetricCard icon={CheckCircle2} label="Receita no período" value={brl(k.revenue_window)} testid="kpi-revwindow" />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-lg border border-border bg-card p-4 lg:col-span-2">
          <h3 className="mb-3 font-heading font-semibold">Ativações & Receita ({a.days} dias)</h3>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={a.series} margin={{ left: -18, right: 8, top: 4 }}>
              <defs>
                <linearGradient id="gA" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
              <Area type="monotone" dataKey="total" name="Ativações" stroke="hsl(var(--primary))" fill="url(#gA)" strokeWidth={2} />
              <Area type="monotone" dataKey="revenue" name="Receita (R$)" stroke="#16a34a" fillOpacity={0} strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-lg border border-border bg-card p-4">
          <h3 className="mb-3 font-heading font-semibold">Top aplicativos</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={a.top_apps} layout="vertical" margin={{ left: 10, right: 8 }}>
              <XAxis type="number" hide allowDecimals={false} />
              <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 10 }} />
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
              <Bar dataKey="count" name="Ativações" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-lg border border-border bg-card p-4">
          <h3 className="mb-3 font-heading font-semibold">Receita por gateway</h3>
          {a.financial.by_provider.length === 0 && <p className="text-sm text-muted-foreground">Sem vendas no período.</p>}
          <div className="space-y-2">
            {a.financial.by_provider.map((p) => (
              <div key={p.provider} className="flex items-center justify-between text-sm" data-testid={`prov-${p.provider}`}>
                <span className="capitalize">{p.provider} · {p.count}x</span>
                <span className="font-num font-semibold text-primary">{brl(p.amount)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-lg border border-border bg-card p-4">
          <h3 className="mb-3 font-heading font-semibold">Top revendedores</h3>
          {a.financial.top_resellers.length === 0 && <p className="text-sm text-muted-foreground">Sem vendas de revenda.</p>}
          <div className="space-y-2">
            {a.financial.top_resellers.map((r, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span className="truncate">{r.name}</span>
                <span className="font-num font-semibold">{brl(r.amount)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-lg border border-border bg-card p-4" data-testid="integrations-health">
          <h3 className="mb-3 font-heading font-semibold">Saúde das integrações</h3>
          <div className="space-y-2">
            {INTEGRATIONS.map((it) => {
              const ok = a.integrations[it.key];
              return (
                <div key={it.key} className="flex items-center justify-between text-sm" data-testid={`integ-${it.key}`}>
                  <span>{it.label}</span>
                  <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${ok ? "text-green-600 dark:text-green-400" : "text-muted-foreground"}`}>
                    <span className={`h-2 w-2 rounded-full ${ok ? "bg-green-500" : "bg-muted-foreground/40"}`} />
                    {ok ? "Configurado" : "Inativo"}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card">
        <div className="border-b border-border p-4"><h3 className="font-heading font-semibold">Atividade recente</h3></div>
        <div className="divide-y divide-border">
          {recent.length === 0 && <p className="p-6 text-center text-sm text-muted-foreground">Sem atividade.</p>}
          {recent.map((act) => (
            <div key={act.id} className="flex items-center justify-between p-4" data-testid={`admin-recent-${act.id}`}>
              <div>
                <p className="text-sm font-medium">{act.app_name}</p>
                <p className="text-xs text-muted-foreground">{act.guest_email || act.user_id || "—"} · {act.mac_app}</p>
              </div>
              <StatusBadge status={act.status} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
